import React, { useState, useRef } from 'react';
import {
    Button,
    Input,
    Upload,
    message,
    DatePicker,
    Cascader,
    Form,
    Card,
    Avatar,
    Popconfirm,
    Tooltip,
    Progress,
    Spin,
    Modal,
    Tabs,
    Radio
} from 'antd';
import {
    UserOutlined,
    CameraOutlined,
    DeleteOutlined,
    InfoCircleOutlined,
    LinkOutlined,
    UploadOutlined,
    ScissorOutlined
} from '@ant-design/icons';
import { Cropper } from 'react-advanced-cropper';
import 'react-advanced-cropper/dist/style.css';
import Config from "../../config";
import dayjs from "dayjs";
import locale from 'antd/lib/date-picker/locale/zh_CN';
import _ from "lodash";
import { useCommonStore } from "../../store/useCommonStore";
import useRequest from "@/utils/useRequest";

const BasicInfoTab = ({ user, areatree }) => {
    const { request } = useRequest();
    const [form] = Form.useForm();
    
    const [loading, setLoading] = useState(false);
    const [avatarLoading, setAvatarLoading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [formChanged, setFormChanged] = useState(false);
    
    // 图片裁剪相关状态
    const [cropperVisible, setCropperVisible] = useState(false);
    const [cropperImage, setCropperImage] = useState(null);
    const [cropperType, setCropperType] = useState('upload'); // 'upload' 或 'url'
    const [imageUrl, setImageUrl] = useState('');
    const cropperRef = useRef(null);
    const fileInputRef = useRef(null);
    
    // 临时头像状态（用于界面显示，未保存到后台）
    const [tempAvatarUrl, setTempAvatarUrl] = useState(null);
    const [pendingAvatarData, setPendingAvatarData] = useState(null); // 待保存的头像数据
    
    // 初始化表单数据
    React.useEffect(() => {
        if (user) {
            form.setFieldsValue({
                Name: user.Name,
                Contact: user.Contact,
                ContactPhone: user.ContactPhone,
                Birthday: user.Birthday ? dayjs(user.Birthday) : null,
                Area: getAreaPath(user.AreaId),
                Address: user.Address
            });
        }
    }, [user]);
    
    // 监听表单变化
    const handleFormChange = () => {
        setFormChanged(true);
    };
    
    // 获取地区路径
    const getAreaPath = (areaId) => {
        if (!areatree || !areaId) return [];
        let area = [];
        let b = [];
        
        const deepSearch = (tree, key) => {
            for (var i = 0; i < tree.length; i++) {
                area.push(tree[i].Id);
                if (tree[i].Id == key) {
                    b = _.clone(area);
                    return;
                }
                if (tree[i].children && tree[i].children.length > 0) {
                    deepSearch(tree[i].children, key);
                }
                area.pop();
            }
        };
        
        deepSearch(areatree, areaId);
        return b;
    };
    
    // 打开裁剪器模态框
    const openCropper = () => {
        setCropperVisible(true);
        setCropperType('upload');
        setCropperImage(null);
        setImageUrl('');
    };
    
    // 关闭裁剪器模态框
    const closeCropper = () => {
        setCropperVisible(false);
        setCropperImage(null);
        setImageUrl('');
    };
    
    // 处理本地图片选择
    const handleLocalImageSelect = (e) => {
        const file = e.target.files[0];
        if (file) {
            // 验证文件类型和大小
            const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png' || file.type === 'image/gif';
            if (!isJpgOrPng) {
                message.error('只能上传 JPG/PNG/GIF 格式的图片!');
                return;
            }
            const isLt2M = file.size / 1024 / 1024 < 2;
            if (!isLt2M) {
                message.error('图片大小不能超过 2MB!');
                return;
            }
            
            // 读取文件并设置到裁剪器
            const reader = new FileReader();
            reader.onload = () => {
                setCropperImage(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };
    
    // 处理网络图片URL输入
    const handleUrlImageSelect = () => {
        if (!imageUrl) {
            message.error('请输入有效的图片URL');
            return;
        }
        setCropperImage(imageUrl);
    };
    
    // 处理裁剪完成
    const handleCropComplete = async () => {
        if (!cropperRef.current) {
            message.error('裁剪失败，请重试');
            return;
        }
        
        try {
            setAvatarLoading(true);
            
            // 获取裁剪后的图片数据
            const canvas = cropperRef.current.getCanvas();
            if (!canvas) {
                throw new Error('无法获取裁剪后的图片');
            }
            
            // 将Canvas转换为Blob
            const blob = await new Promise(resolve => {
                canvas.toBlob(blob => resolve(blob), 'image/jpeg', 0.95);
            });
            
            // 创建FormData并上传
            const formData = new FormData();
            formData.append('avatar', blob, 'cropped-avatar.jpg');
            
            // 上传图片
            const uploadResponse = await fetch(`${Config.ApiHost}/api/v5/private/upload-image-manual`, {
                method: 'POST',
                body: formData,
            }).then(res => res.json());
            
            if (uploadResponse.success && uploadResponse.data && uploadResponse.data.length > 0) {
                // 获取上传后的图片信息
                const imageData = uploadResponse.data[0];
                // 构建完整的图片URL
                const fullImageUrl = `${Config.ApiHost}${imageData.url}`;
                
                // 设置临时头像用于界面显示
                setTempAvatarUrl(fullImageUrl);
                // 保存待提交的头像数据
                setPendingAvatarData(imageData.url);
                // 标记表单已更改
                setFormChanged(true);
                
                message.success('头像已选择，点击保存基本信息完成更新');
                closeCropper();
            } else {
                throw new Error(uploadResponse.message || '上传失败');
            }
        } catch (error) {
            console.error('头像上传错误:', error);
            message.error('头像上传失败: ' + (error.message || '未知错误'));
        } finally {
            setAvatarLoading(false);
        }
    };
    
    // 头像上传前验证
    const beforeUpload = (file) => {
        const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png' || file.type === 'image/gif';
        if (!isJpgOrPng) {
            message.error('只能上传 JPG/PNG/GIF 格式的图片!');
            return false;
        }
        const isLt2M = file.size / 1024 / 1024 < 2;
        if (!isLt2M) {
            message.error('图片大小不能超过 2MB!');
            return false;
        }
        
        // 阻止默认上传行为，改为打开裁剪器
        const reader = new FileReader();
        reader.onload = () => {
            setCropperImage(reader.result);
            setCropperVisible(true);
            setCropperType('upload');
        };
        reader.readAsDataURL(file);
        
        return false; // 阻止默认上传
    };
    

    
    // 移除头像（仅在界面上移除，保存时生效）
    const removeAvatar = () => {
        setTempAvatarUrl(null);
        setPendingAvatarData(''); // 设置为空字符串表示要移除头像
        setFormChanged(true);
        message.success('头像已移除，点击保存基本信息完成更新');
    };
    
    // 保存基本信息
    const saveProfile = async (values) => {
        setLoading(true);
        try {
            const submitData = {
                ...values,
                Birthday: values.Birthday ? values.Birthday.format('YYYY-MM-DD') : null,
                AreaId: values.Area && values.Area.length > 0 ? parseInt(values.Area[values.Area.length - 1]) : null
            };
            
            // 如果有待保存的头像数据，添加到提交数据中
            if (pendingAvatarData !== null) {
                submitData.HeadImgUrl = pendingAvatarData; // 可能是新头像URL或空字符串（移除头像）
            }
            
            const result = await request('/api/v2/private/user/profile/update', 'POST', 'json', submitData);
            if (result.success) {
                message.success('用户资料更新成功');
                setFormChanged(false);
                // 清除临时头像状态
                setTempAvatarUrl(null);
                setPendingAvatarData(null);
                const commonStore = useCommonStore.getState();
                commonStore.initFn();
            } else {
                message.error(result.message || '保存失败');
            }
        } catch (err) {
            console.error('保存个人信息错误:', err);
            message.error('保存失败，请重试');
        } finally {
            setLoading(false);
        }
    };
    
    return (
        <>
        <Card>
            <div className="flex flex-col lg:flex-row gap-8">
                {/* 头像部分 */}
                <div className="flex flex-col items-center space-y-4">
                    <div className="relative">
                        <Avatar
                            size={120}
                            src={
                                tempAvatarUrl || 
                                (pendingAvatarData === '' ? null : 
                                    (user.HeadImgUrl ? `${Config.ApiHost}${user.HeadImgUrl}` : null)
                                )
                            }
                            icon={<UserOutlined />}
                            className="border-4 border-gray-200 shadow-lg"
                        />
                        {avatarLoading && (
                            <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 rounded-full">
                                <Spin size="large" />
                            </div>
                        )}
                    </div>
                    
                    {uploadProgress > 0 && uploadProgress < 100 && (
                        <div className="w-32">
                            <Progress 
                                percent={uploadProgress} 
                                size="small" 
                                status="active"
                                showInfo={false}
                            />
                            <div className="text-xs text-center text-gray-500 mt-1">
                                上传中... {Math.round(uploadProgress)}%
                            </div>
                        </div>
                    )}
                    
                    <div className="flex space-x-2">
                        <Tooltip title="支持 JPG、PNG、GIF 格式，文件大小不超过 2MB">
                            <Button 
                                icon={<ScissorOutlined />} 
                                onClick={openCropper} 
                                loading={avatarLoading}
                            >
                                编辑头像
                            </Button>
                        </Tooltip>
                        {(tempAvatarUrl || (user.HeadImgUrl && pendingAvatarData !== '')) && (
                            <Popconfirm
                                title="确定要移除头像吗？"
                                description="移除后将显示默认头像"
                                onConfirm={removeAvatar}
                                okText="确定"
                                cancelText="取消"
                            >
                                <Button icon={<DeleteOutlined />} danger>
                                    移除
                                </Button>
                            </Popconfirm>
                        )}
                    </div>
                </div>
                
                {/* 基本信息表单 */}
                <div className="flex-1">
                    <Form
                        form={form}
                        layout="vertical"
                        onFinish={saveProfile}
                        onValuesChange={handleFormChange}
                        className="space-y-4"
                    >
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <Form.Item
                                label="姓名"
                                name="Name"
                                rules={[{ required: true, message: '请输入姓名' }]}
                            >
                                <Input placeholder="请输入姓名" />
                            </Form.Item>
                            
                            <Form.Item
                                label="联系人"
                                name="Contact"
                                rules={[{ required: true, message: '请输入联系人' }]}
                            >
                                <Input placeholder="请输入联系人" />
                            </Form.Item>
                            
                            <Form.Item
                                label="联系电话"
                                name="ContactPhone"
                                rules={[{ required: true, message: '请输入联系电话' }]}
                            >
                                <Input placeholder="请输入联系电话" />
                            </Form.Item>
                            
                            <Form.Item
                                label="生日"
                                name="Birthday"
                            >
                                <DatePicker
                                    style={{ width: '100%' }}
                                    locale={locale}
                                    placeholder="请选择生日"
                                />
                            </Form.Item>
                            
                            <Form.Item
                                label="地区"
                                name="Area"
                            >
                                <Cascader
                                    fieldNames={{ label: 'Name', value: 'Id', children: 'children' }}
                                    options={areatree}
                                    placeholder="请选择地区"
                                />
                            </Form.Item>
                            
                            <Form.Item
                                label="详细地址"
                                name="Address"
                            >
                                <Input placeholder="请输入详细地址" />
                            </Form.Item>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <Form.Item label="所属企业">
                                <Input disabled value={user.Org?.name} />
                            </Form.Item>
                            
                            <Form.Item label="角色">
                                <Input disabled value={_(user.CustomerRoles).map(item => item.Name).join(',')} />
                            </Form.Item>
                        </div>
                        
                        <Form.Item>
                            <div className="flex items-center space-x-4">
                                <Button 
                                    type="primary" 
                                    htmlType="submit" 
                                    loading={loading}
                                    className={formChanged ? 'animate-pulse' : ''}
                                >
                                    保存基本信息
                                </Button>
                                {formChanged && (
                                    <span className="text-orange-500 text-sm">
                                        <InfoCircleOutlined className="mr-1" />
                                        有未保存的更改{pendingAvatarData !== null ? '（包含头像变更）' : ''}
                                    </span>
                                )}
                            </div>
                        </Form.Item>
                    </Form>
                </div>
            </div>
        </Card>
        
        {/* 图片裁剪模态框 */}
        <CropperModal
            visible={cropperVisible}
            onCancel={closeCropper}
            image={cropperImage}
            cropperType={cropperType}
            setCropperType={setCropperType}
            imageUrl={imageUrl}
            setImageUrl={setImageUrl}
            onComplete={handleCropComplete}
            cropperRef={cropperRef}
            fileInputRef={fileInputRef}
            onLocalImageSelect={handleLocalImageSelect}
            onUrlImageSelect={handleUrlImageSelect}
            loading={avatarLoading}
        />
        </>
    );
};

// 图片裁剪模态框组件
const CropperModal = ({ 
    visible, 
    onCancel, 
    image, 
    cropperType, 
    setCropperType, 
    imageUrl, 
    setImageUrl, 
    onComplete, 
    cropperRef, 
    fileInputRef, 
    onLocalImageSelect, 
    onUrlImageSelect, 
    loading 
}) => {
    return (
        <Modal
            title="编辑头像"
            open={visible}
            onCancel={onCancel}
            width={800}
            footer={[
                <Button key="cancel" onClick={onCancel}>
                    取消
                </Button>,
                <Button 
                    key="submit" 
                    type="primary" 
                    onClick={onComplete} 
                    disabled={!image}
                    loading={loading}
                >
                    确认
                </Button>
            ]}
        >
            <Tabs
                activeKey={cropperType}
                onChange={setCropperType}
                items={[
                    {
                        key: 'upload',
                        label: (
                            <span>
                                <UploadOutlined /> 本地上传
                            </span>
                        ),
                        children: (
                            <div className="p-4">
                                <input
                                    type="file"
                                    accept="image/jpeg,image/png,image/gif"
                                    onChange={onLocalImageSelect}
                                    ref={fileInputRef}
                                    style={{ display: 'none' }}
                                />
                                <Button 
                                    onClick={() => fileInputRef.current?.click()}
                                    icon={<UploadOutlined />}
                                >
                                    选择图片
                                </Button>
                                <div className="text-gray-500 text-sm mt-2">
                                    支持 JPG、PNG、GIF 格式，文件大小不超过 2MB
                                </div>
                            </div>
                        ),
                    },
                    {
                        key: 'url',
                        label: (
                            <span>
                                <LinkOutlined /> 网络图片
                            </span>
                        ),
                        children: (
                            <div className="p-4">
                                <Input
                                    placeholder="请输入图片URL"
                                    value={imageUrl}
                                    onChange={(e) => setImageUrl(e.target.value)}
                                    addonAfter={
                                        <Button 
                                            type="link" 
                                            size="small" 
                                            onClick={onUrlImageSelect}
                                            style={{ margin: '-4px -12px' }}
                                        >
                                            确认
                                        </Button>
                                    }
                                />
                                <div className="text-gray-500 text-sm mt-2">
                                    请输入有效的图片URL地址
                                </div>
                            </div>
                        ),
                    },
                ]}
            />
            
            <div className="mt-4 flex justify-center">
                {image ? (
                    <div style={{ width: '100%', height: '400px', maxWidth: '600px' }}>
                        <Cropper
                            ref={cropperRef}
                            src={image}
                            className="cropper"
                            stencilProps={{
                                aspectRatio: 1
                            }}
                        />
                    </div>
                ) : (
                    <div className="flex items-center justify-center h-64 w-full bg-gray-100 rounded-md">
                        <div className="text-center text-gray-500">
                            <UserOutlined style={{ fontSize: '48px' }} />
                            <div className="mt-2">请选择或输入图片</div>
                        </div>
                    </div>
                )}
            </div>
        </Modal>
    );
};

// 导出原始组件
export default BasicInfoTab;